import { type Component, For, type JSX, Show } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import type { LucideProps } from 'lucide-solid';

export interface RailChildItem {
  href: string;
  label: string;
  unavailable?: string;
}

export interface RailItem {
  href: string;
  label: string;
  icon?: Component<LucideProps>;
  exact?: boolean;
  children?: RailChildItem[];
}

export interface RailAccent {
  solid: string;
  soft?: string;
  hairline?: string;
}

export interface RailGroup {
  key: string;
  items: RailItem[];
  accent?: RailAccent;
}

export interface SectionRailProps {
  groups: RailGroup[];
  ariaLabel: string;
  accent: string;
  accentSoft?: string;
  accentHairline?: string;
  widthClass?: string;
  orientation?: 'responsive' | 'column';
}

const mix = (solid: string, pct: number): string =>
  `color-mix(in srgb, ${solid} ${pct}%, transparent)`;

const accentOf = (p: SectionRailProps, g: RailGroup): Required<RailAccent> =>
  g.accent
    ? {
        solid: g.accent.solid,
        soft: g.accent.soft ?? mix(g.accent.solid, 14),
        hairline: g.accent.hairline ?? mix(g.accent.solid, 32),
      }
    : {
        solid: p.accent,
        soft: p.accentSoft ?? mix(p.accent, 14),
        hairline: p.accentHairline ?? mix(p.accent, 32),
      };

const matches = (pathname: string, item: RailItem): boolean =>
  item.exact ? pathname === item.href : pathname.startsWith(item.href);

export const SectionRail: Component<SectionRailProps> = (props) => {
  const location = useLocation();
  const isOpen = (item: RailItem) => matches(location.pathname, item);
  const asColumn = () => props.orientation === 'column';

  const stacked = () => (asColumn() ? 'flex' : 'hidden lg:flex');

  return (
    <nav
      aria-label={props.ariaLabel}
      class={
        asColumn()
          ? `flex shrink-0 flex-col gap-1 overflow-y-auto pr-1 ${props.widthClass ?? 'w-full'}`
          : `flex shrink-0 gap-1 overflow-x-auto lg:flex-col lg:overflow-x-visible lg:overflow-y-auto lg:pr-1 ${
              props.widthClass ?? 'lg:w-[196px]'
            }`
      }
    >
      <For each={props.groups}>
        {(group, groupIndex) => {
          const acc = () => accentOf(props, group);
          return (
          <>
            <Show when={groupIndex() > 0}>
              <div
                class={`${group.accent ? 'my-2.5' : 'my-1'} h-px shrink-0 ${asColumn() ? 'block' : 'hidden lg:block'}`}
                style={{ background: 'var(--color-border-hairline)' }}
                aria-hidden="true"
              />
            </Show>

            <For each={group.items}>
              {(item) => (
                <>
                  <A
                    href={item.href}
                    end={item.exact === true}
                    class="group flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-2 text-[13px] leading-tight transition-colors"
                    style={
                      isOpen(item)
                        ? {
                            background: acc().soft,
                            'box-shadow': `inset 0 0 0 1px ${acc().hairline}`,
                            color: 'var(--color-text-primary)',
                          }
                        : { color: 'var(--color-text-secondary)' }
                    }
                  >
                    <Show when={item.icon}>
                      {(Icon) => (
                        <Dyn
                          icon={Icon()}
                          tint={
                            isOpen(item) || group.accent ? acc().solid : 'var(--color-text-tertiary)'
                          }
                        />
                      )}
                    </Show>
                    <span class="whitespace-nowrap lg:whitespace-normal">{item.label}</span>
                  </A>

                  <Show when={isOpen(item) && (item.children?.length ?? 0) > 0}>
                    <ul
                      class={`mb-1 ml-[22px] flex-col pl-3 ${stacked()}`}
                      style={{ 'border-left': '1px solid var(--color-border-hairline)' }}
                    >
                      <For each={item.children}>
                        {(child) => (
                          <li>
                            <Show
                              when={!child.unavailable}
                              fallback={
                                <span
                                  class="block cursor-default px-2.5 py-[7px] text-[12.5px] leading-tight opacity-55"
                                  style={{ color: 'var(--color-text-tertiary)' }}
                                  title={child.unavailable}
                                >
                                  {child.label}
                                  <span class="ml-1.5 text-[10px]">скоро</span>
                                </span>
                              }
                            >
                              <A
                                href={child.href}
                                end
                                class="block rounded-[var(--radius-xs)] px-2.5 py-[7px] text-[12.5px] leading-tight transition-colors"
                                style={
                                  location.pathname === child.href
                                    ? { background: acc().soft, color: acc().solid, 'font-weight': 600 }
                                    : { color: 'var(--color-text-secondary)' }
                                }
                              >
                                {child.label}
                              </A>
                            </Show>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </>
              )}
            </For>
          </>
          );
        }}
      </For>
    </nav>
  );
};

const Dyn: Component<{ icon: Component<LucideProps>; tint: string }> = (props) => {
  const Icon = props.icon;
  return <Icon class="h-3.5 w-3.5 shrink-0" style={{ color: props.tint }} />;
};

export type SectionRailSlot = JSX.Element;
